import { A11yModule } from '@angular/cdk/a11y';
import { Component, DestroyRef, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BaseCellRendererComponent } from '@examdojo/ag-grid';
import { ButtonComponent } from '@examdojo/core/button';
import { ErrorHandlerService } from '@examdojo/core/error-handling';
import { IconComponent } from '@examdojo/core/icon';
import { TextareaInputComponent } from '@examdojo/core/input';
import { ToastService } from '@examdojo/core/toast';
import { PayoutRequestService, PayoutRequestUIModel, ReceiptViewerDialogService } from '@examdojo/payments';
import { ExamdojoSupabaseService } from '@examdojo/supabase';
import { EMPTY } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';

@Component({
  selector: 'exam-payment-request-actions-cell-renderer',
  template: `
    <div class="flex h-full items-center justify-end gap-2">
      @if (hasInvoice) {
        <dojo-button [fill]="'solid'" [size]="'xs'" [customColor]="'orange'" (click)="onViewInvoice($event)">
          <dojo-icon [icon]="'file-pdf'" class="mr-1" [size]="14" />
          View Invoice
        </dojo-button>
      }

      @if (canShowActions) {
        <dojo-button
          [size]="'xs'"
          [fill]="'solid'"
          [customColor]="'green'"
          [matMenuTriggerFor]="approveMenu"
          #approveTrigger="matMenuTrigger"
          (click)="$event.stopPropagation()"
        >
          <dojo-icon [icon]="'check'" class="mr-1" [size]="14" />
          Approve
        </dojo-button>

        <dojo-button
          [size]="'xs'"
          [fill]="'outline'"
          [customColor]="'red'"
          [matMenuTriggerFor]="rejectMenu"
          #rejectTrigger="matMenuTrigger"
          (click)="$event.stopPropagation()"
        >
          <dojo-icon [icon]="'times'" class="mr-1" [size]="14" />
          Reject
        </dojo-button>
      }
    </div>

    <mat-menu #approveMenu="matMenu" class="custom-mat-menu">
      <ng-template matMenuContent>
        <div class="flex flex-col gap-3 p-4" cdkTrapFocus cdkTrapFocusAutoCapture (click)="$event.stopPropagation()">
          <h3 class="text-md font-semibold">Approve Payment Request</h3>
          <p class="text-sm text-gray-600">
            Are you sure you want to approve this payment request for {{ params?.data?.user_name }}?
          </p>

          <div class="flex flex-col gap-2">
            <label class="text-sm font-medium">Additional Comments (Optional)</label>
            <y42-textarea-input
              [autoResize]="true"
              [formCtrl]="approvalForm.controls.reason"
              placeholder="Enter approval comments..."
            />
          </div>

          <div class="flex gap-2">
            <dojo-button [size]="'s'" [fill]="'outline'" [customWidth]="80" (click)="closeMenus()">
              Cancel
            </dojo-button>
            <dojo-button [size]="'s'" [customWidth]="80" [pending]="isApproving()" (click)="confirmApprove()">
              Approve
            </dojo-button>
          </div>
        </div>
      </ng-template>
    </mat-menu>

    <mat-menu #rejectMenu="matMenu" class="custom-mat-menu">
      <ng-template matMenuContent>
        <div class="flex flex-col gap-3 p-4" cdkTrapFocus cdkTrapFocusAutoCapture (click)="$event.stopPropagation()">
          <h3 class="text-md font-semibold">Reject Payment Request</h3>
          <p class="text-sm text-gray-600">
            Please provide a reason for rejecting this payment request for {{ params?.data?.user_name }}.
          </p>

          <div class="flex flex-col gap-2">
            <label class="text-sm font-medium">Rejection Reason *</label>
            <y42-textarea-input
              [autoResize]="true"
              [formCtrl]="rejectionForm.controls.reason"
              placeholder="Enter rejection reason..."
            />
          </div>

          <div class="flex gap-2">
            <dojo-button [size]="'s'" [fill]="'outline'" [customWidth]="80" (click)="closeMenus()">
              Cancel
            </dojo-button>
            <dojo-button
              [size]="'s'"
              [customColor]="'red'"
              [customWidth]="80"
              [pending]="isRejecting()"
              [disabled]="rejectionForm.invalid"
              (click)="confirmReject()"
            >
              Reject
            </dojo-button>
          </div>
        </div>
      </ng-template>
    </mat-menu>
  `,
  imports: [
    MatTooltipModule,
    MatMenuModule,
    ButtonComponent,
    IconComponent,
    TextareaInputComponent,
    ReactiveFormsModule,
    A11yModule,
  ],
})
export class PaymentRequestActionsCellRendererComponent extends BaseCellRendererComponent<
  unknown,
  PayoutRequestUIModel
> {
  private readonly payoutRequestService = inject(PayoutRequestService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly errorHandlerService = inject(ErrorHandlerService);
  private readonly toastService = inject(ToastService);
  private readonly receiptViewerDialogService = inject(ReceiptViewerDialogService);
  private readonly supabaseService = inject(ExamdojoSupabaseService);

  readonly approvalForm = new FormGroup({
    reason: new FormControl<string>('', { nonNullable: true }),
  });

  readonly rejectionForm = new FormGroup({
    reason: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
  });

  readonly approveTrigger = viewChild<MatMenuTrigger>('approveTrigger');
  readonly rejectTrigger = viewChild<MatMenuTrigger>('rejectTrigger');

  readonly isApproving = signal(false);
  readonly isRejecting = signal(false);

  get request(): PayoutRequestUIModel | undefined {
    return this.params?.data;
  }

  get canShowActions(): boolean {
    return this.params?.data?.status === 'WAITING_FOR_APPROVAL';
  }

  get hasInvoice(): boolean {
    const status = this.request?.status;

    if (status !== 'PAYMENT_SUCCEEDED') {
      return false;
    }

    const attachments = this.request?.attachments as Array<{ url: string; type: string }> | null | undefined;
    if (!Array.isArray(attachments)) {
      return false;
    }

    const invoice = attachments.find((att) => att.type === 'invoice');
    return !!invoice?.url;
  }

  async onViewInvoice(event: Event): Promise<void> {
    event.stopPropagation();
    if (!this.request) {
      return;
    }

    const attachments = this.request.attachments as Array<{ url: string; type: string }> | null | undefined;

    if (!Array.isArray(attachments)) {
      this.toastService.error('Invoice not available');
      return;
    }

    const invoice = attachments.find((att) => att.type === 'invoice');
    if (!invoice?.url) {
      this.toastService.error('Invoice not available');
      return;
    }

    try {
      const s3Url = invoice.url;
      const s3UrlMatch = s3Url.match(/^s3:\/\/([^/]+)\/(.+)$/);

      if (!s3UrlMatch) {
        this.toastService.error('Invalid invoice URL format');
        return;
      }

      const bucketName = s3UrlMatch[1];
      const filePath = s3UrlMatch[2];

      const signedUrl = await this.supabaseService.getSignedUrlForFile(bucketName, filePath);

      await this.receiptViewerDialogService.openDialog({
        input: {
          receiptUrl: signedUrl,
          period: this.request.period,
        },
      });
    } catch (error) {
      console.error('Error opening invoice:', error);
      this.toastService.error('Failed to load invoice');
    }
  }

  closeMenus() {
    this.isApproving.set(false);
    this.isRejecting.set(false);

    this.approvalForm.reset();
    this.rejectionForm.reset();

    this.approveTrigger()?.closeMenu();
    this.rejectTrigger()?.closeMenu();
  }

  confirmApprove() {
    if (!this.params?.data?.id) {
      return;
    }

    this.isApproving.set(true);

    const reason = this.approvalForm.controls.reason.value || '';

    this.payoutRequestService
      .approvePayoutRequest(this.params.data.id, reason)
      .pipe(
        tap(() => {
          if (this.params?.data) {
            this.params.data.status_reason = reason;
            this.params.data.status = 'APPROVED';

            this.closeMenus();

            this.toastService.success('Payment request approved');
          }
        }),
        this.errorHandlerService.catchHttpErrors(() => EMPTY),
        finalize(() => this.isApproving.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  confirmReject() {
    if (!this.params?.data?.id) {
      return;
    }

    this.rejectionForm.markAllAsTouched();

    if (this.rejectionForm.invalid) {
      return;
    }

    this.isRejecting.set(true);

    const reason = this.rejectionForm.controls.reason.value;

    this.payoutRequestService
      .rejectPayoutRequest(this.params.data.id, reason)
      .pipe(
        tap(() => {
          if (this.params?.data) {
            this.params.data.status_reason = reason;
            this.params.data.status = 'REJECTED';

            this.closeMenus();

            this.toastService.success('Payment request rejected');
          }
        }),
        this.errorHandlerService.catchHttpErrors(() => EMPTY),
        finalize(() => this.isRejecting.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }
}
